import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { Settings } from '@/types/settings';
import { useAuthStore } from './auth.store.ts';

export const DEFAULT_SETTINGS: Settings = {
  // Messaging & Customer Treatment
  greetingMode: 'dynamic',
  greetingPlainPrefix: 'Hola',
  customerNameMode: 'real',
  customerAlias: 'cariño',
  nameMode: 'first',
  messageTimeMode: 'start_only',
  messageRangeConnector: 'hasta',
  timeMode: '12h',
  showServices: true,
  closingText: '¡Te esperamos! Cualquier cambio nos avisas.',
  includeLastVisitSummaryInMessage: false,
  messageUseFriendlyDate: true,
  messageIncludeDayName: true,
  messageUseTomorrowTodayLabels: true,

  // WhatsApp
  whatsappMode: 'api',
  reviewLink: '',

  // Message Templates - Appointments
  templateReminder: '*{saludo} {cliente}* ✨\n\nTe recordamos tu cita {de_servicios} con *{profesional}*:\n🗓️ *{fecha_hora}*\n📍 Sede: *{sede}*{precio_bloque}\n\nPor favor respóndenos para confirmar tu asistencia o avísanos si requieres reprogramar.\n\n{firma}{extra}',
  templateNew: '*{saludo} {cliente}* 🎉\n\n¡Tu cita {de_servicios} ha quedado reservada con éxito en *{negocio}*!\n\n🗓️ *Fecha y hora:* {fecha_hora}\n💇‍♀️ *Especialista:* *{profesional}*\n📍 *Sede:* *{sede}*{precio_bloque}\n\n¡Estamos listos para consentirte! {firma}{extra}',
  templateArrived: '*{saludo} {cliente}* ☕✨\n\n¡Bienvenida a *{negocio}*! Ya registramos tu llegada.\n\n*{profesional}* se está preparando y estará contigo en unos momentos. Mientras tanto, ponte cómoda en nuestra sala de espera. 💛',
  templateStarted: '*{saludo} {cliente}* 💆‍♀️✨\n\nTu sesión {de_servicios} con *{profesional}* ha comenzado en *{negocio}*.\n\nRelájate y disfruta tu momento de cuidado personal. ¡Estamos para consentirte! 🌿',
  templateCompleted: '*{saludo} {cliente}* 💛\n\nMuchas gracias por visitarnos hoy en *{negocio}*. Fue un verdadero placer atenderte en tu cita {de_servicios} con *{profesional}*.\n\nEsperamos que te sientas increíble con tus resultados. ¡Estamos a tu orden para cualquier duda sobre el cuidado de tu servicio! ✨{precio_bloque}{extra}',
  templateCompletedReview: '*{saludo} {cliente}* ⭐\n\n¡Muchas gracias por tu visita a *{negocio}*! Esperamos que hayas disfrutado tu experiencia {de_servicios} con *{profesional}*.\n\n{link_resena}{precio_bloque}{extra}',
  templateCompletedRebook: '*{saludo} {cliente}* ✨\n\nEsperamos que sigas disfrutando los resultados de tu servicio {de_servicios}.\n\nPara mantener tu tratamiento siempre impecable, te sugerimos apartar tu próximo espacio con *{profesional}* con anticipación en *{negocio}*.\n\n🗓️ ¿Te gustaría agendar tu próxima sesión para las próximas semanas? Escríbenos y con gusto te mostramos la disponibilidad. 💛{precio_bloque}{extra}',
  templateCompletedGeneral: '*{saludo} {cliente}* 💛\n\nMuchas gracias por visitarnos hoy en *{negocio}*. Fue un verdadero placer atenderte en tu cita {de_servicios} con *{profesional}*.\n\nEsperamos que te sientas increíble con tus resultados. ¡Estamos a tu orden para cualquier duda sobre el cuidado de tu servicio! ✨{precio_bloque}{extra}',
  completedMessageMode: 'auto',
  templateNoShow: '*{saludo} {cliente}* 🤍\n\nNotamos que no pudiste llegar a tu cita {de_servicios} programada {para_fecha_hora} en *{negocio}*. Esperamos de corazón que todo esté muy bien.\n\nSabemos que pueden ocurrir eventualidades. Cuando estés disponible, escríbenos y con gusto te ayudamos a reprogramar tu espacio con *{profesional}*. ¡Aquí te esperamos! ✨{precio_bloque}{extra}',
  templateCancelled: '*{saludo} {cliente}* 🌿\n\nLamentamos que no hayamos podido coincidir en tu cita {de_servicios} programada {para_fecha_hora} en *{negocio}*.\n\nEntendemos que surgen imprevistos. ¿Te gustaría que busquemos un nuevo horario que te sea más cómodo esta semana? Respóndenos a este mensaje y con gusto lo coordinamos. 💛{precio_bloque}{extra}',

  // Message Templates - Campaigns
  templateCampaignLapsed: '💬 *{saludo} {cliente}*, te escribimos de *{negocio}*.\n\nHace un tiempo que no te vemos por el salón. Tu último servicio fue *{ultimo_servicio}* con *{profesional}*. ¿Te gustaría agendar un espacio esta semana?',
  templateCampaignVip: '⭐ *{saludo} {cliente}*, te saludamos de *{negocio}*.\n\nQueríamos agradecerte por tu preferencia constante con nosotros. Es un placer atenderte. ¿Te gustaría agendar tu próxima sesión con *{profesional}*?',
  templateCampaignDebtor: '📋 *{saludo} {cliente}*, te escribimos cordialmente de *{negocio}*.\n\nQueríamos recordarte amablemente que posees un saldo pendiente de *{deuda}* (*{deuda_bs}*). Por favor contáctanos para coordinar los detalles. ¡Muchas gracias!',
  templateCrmDefault: '👋 *¡Hola {cliente}!* Te saludamos de *{negocio}*. ¿En qué podemos ayudarte el día de hoy?\n\n{firma}',
  msgTemplateBirthday: '🎂 *¡Feliz cumpleaños, {primer_nombre}!* 🎉\n\nDe parte de todo el equipo de *{negocio}*, te deseamos un día maravilloso. ¡Te esperamos pronto para consentirte!',
  msgTemplateTopSpenders: '💎 *{saludo} {cliente}*, te saludamos de *{negocio}*.\n\nEres uno de nuestros clientes más especiales. Queremos agradecer tu preferencia y asegurarnos de que sigas disfrutando de la mejor atención con *{profesional}*. ¡Quedamos a tu completa disposición!',
  msgTemplateNewClients: '👋 *{saludo} {cliente}*, te saludamos de *{negocio}*.\n\nMuchas gracias por visitarnos en *{negocio}*. Esperamos que hayas disfrutado tu experiencia con *{profesional}*. ¡Cualquier consulta estamos a tu completa orden!',
  msgTemplateDebtor: '📋 *{saludo} {cliente}*, te escribimos cordialmente de *{negocio}*.\n\nQueríamos recordarte amablemente que posees un saldo pendiente de *{deuda}* (*{deuda_bs}*). Por favor contáctanos para coordinar los detalles. ¡Muchas gracias!',
  msgTemplateVip: '⭐ *{saludo} {cliente}*, te saludamos de *{negocio}*.\n\nQueríamos agradecerte por tu preferencia constante con nosotros. Es un placer atenderte. ¿Te gustaría agendar tu próxima sesión con *{profesional}*?',
  msgTemplateLapsed: '💬 *{saludo} {cliente}*, te escribimos de *{negocio}*.\n\nHace un tiempo que no te vemos por el salón. Tu último servicio fue *{ultimo_servicio}* con *{profesional}*. ¿Te gustaría agendar un espacio esta semana?',
  crmSegmentTemplates: {},

  // Currency & Prices
  currencyDisplayMode: 'auto',
  currencyCode: 'USD',
  manualRate: 0,
  recargo: 0,
  showBsPrice: true,
  showLocalBsRate: true,
  historicalRateFillMode: 'next',
  messagePriceMode: 'off',
  messagePriceFormat: 'both',

  // Campaigns
  inactivityDays: 60,
  campaignRangeDays: 365,
  vipMinAppointments: 10,
  vipMinSales: 250,
  vipRelation: 'or',
  debtorMinAmount: 1,

  // App & General
  theme: 'system',
  defaultLocationId: '',
  visibleStatuses: ['new', 'unconfirmed', 'confirmed', 'arrived', 'started', 'completed', 'no-show', 'cancelled'],
  locationAliases: {},
  locationNameMode: 'alias',

  // Auto-refresh & Performance
  autoRefreshEnabled: false,
  autoRefreshIntervalMs: 700,
  detailConcurrency: 6,
  requestDelayMs: 80,
};

export interface SettingsState extends Settings {
  isSyncing: boolean;
  lastSyncedAt: string | null;

  // Actions
  setSetting: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
  setSettings: (settings: Partial<Settings>) => void;
  resetSettings: () => void;
  getSetting: <K extends keyof Settings>(key: K) => Settings[K];
  updateCrmSegmentTemplate: (segmentId: string, template: string) => void;
  removeCrmSegmentTemplate: (segmentId: string) => void;
  fetchRemoteSettings: () => Promise<void>;
  saveRemoteSettings: () => Promise<{ success: boolean; error?: string }>;
}

// Cross-Tab Real-time Broadcast Channel
const SYNC_CHANNEL_NAME = 'fresha_settings_sync';
let syncChannel: BroadcastChannel | null = null;

if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
  try {
    syncChannel = new BroadcastChannel(SYNC_CHANNEL_NAME);
  } catch {
    // Graceful fallback for environments without BroadcastChannel
  }
}

function broadcastSettings(settings: Partial<Settings>, lastSyncedAt: string | null) {
  if (syncChannel) {
    try {
      syncChannel.postMessage({
        type: 'FRESHA_SETTINGS_SYNC',
        payload: settings,
        lastSyncedAt,
        timestamp: Date.now(),
      });
    } catch {
      // ignore
    }
  }
}

export const useSettingsStore = create<SettingsState>()(
  devtools(
    persist(
      (set, get) => ({
        ...DEFAULT_SETTINGS,
        isSyncing: false,
        lastSyncedAt: null,

        setSetting: (key, value) => {
          const updates: any = { [key]: value };
          if (key === 'msgTemplateLapsed') updates.templateCampaignLapsed = value;
          if (key === 'templateCampaignLapsed') updates.msgTemplateLapsed = value;
          if (key === 'msgTemplateVip') updates.templateCampaignVip = value;
          if (key === 'templateCampaignVip') updates.msgTemplateVip = value;
          if (key === 'msgTemplateDebtor') updates.templateCampaignDebtor = value;
          if (key === 'templateCampaignDebtor') updates.msgTemplateDebtor = value;
          set(updates);
        },
        setSettings: (settings) => {
          const updates: any = { ...settings };
          if (updates.msgTemplateLapsed && !updates.templateCampaignLapsed) updates.templateCampaignLapsed = updates.msgTemplateLapsed;
          if (updates.templateCampaignLapsed && !updates.msgTemplateLapsed) updates.msgTemplateLapsed = updates.templateCampaignLapsed;
          if (updates.msgTemplateVip && !updates.templateCampaignVip) updates.templateCampaignVip = updates.msgTemplateVip;
          if (updates.templateCampaignVip && !updates.msgTemplateVip) updates.msgTemplateVip = updates.templateCampaignVip;
          if (updates.msgTemplateDebtor && !updates.templateCampaignDebtor) updates.templateCampaignDebtor = updates.msgTemplateDebtor;
          if (updates.templateCampaignDebtor && !updates.msgTemplateDebtor) updates.msgTemplateDebtor = updates.templateCampaignDebtor;
          set((state) => ({ ...state, ...updates }));
        },
        resetSettings: () => set(DEFAULT_SETTINGS),
        getSetting: (key) => get()[key],

        updateCrmSegmentTemplate: (segmentId, template) =>
          set((state) => ({
            crmSegmentTemplates: {
              ...state.crmSegmentTemplates,
              [segmentId]: template,
            },
          })),

        removeCrmSegmentTemplate: (segmentId) =>
          set((state) => {
            const next = { ...state.crmSegmentTemplates };
            delete next[segmentId];
            return { crmSegmentTemplates: next };
          }),

        fetchRemoteSettings: async () => {
          const token = useAuthStore.getState().token;
          if (!token) return;

          set({ isSyncing: true });
          try {
            const res = await fetch('/api/settings', {
              headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
              },
            });
            const result = await res.json().catch(() => ({}));
            if (res.ok && result.success && result.data) {
              const { updatedAt, updatedBy: _updatedBy, ...remoteSettings } = result.data;
              set((state) => ({
                ...state,
                ...remoteSettings,
                lastSyncedAt: updatedAt || new Date().toISOString(),
                isSyncing: false,
              }));
            } else {
              set({ isSyncing: false });
            }
          } catch (err) {
            console.error('Failed to fetch remote settings', err);
            set({ isSyncing: false });
          }
        },

        saveRemoteSettings: async () => {
          const token = useAuthStore.getState().token;
          if (!token) {
            return { success: false, error: 'Tu sesión ha caducado. Por favor inicia sesión nuevamente.' };
          }

          set({ isSyncing: true });
          try {
            const state = get();
            const {
              isSyncing: _isSyncing,
              lastSyncedAt: _lastSyncedAt,
              setSetting: _setSetting,
              setSettings: _setSettings,
              resetSettings: _resetSettings,
              getSetting: _getSetting,
              updateCrmSegmentTemplate: _updateCrmSegmentTemplate,
              removeCrmSegmentTemplate: _removeCrmSegmentTemplate,
              fetchRemoteSettings: _fetchRemoteSettings,
              saveRemoteSettings: _saveRemoteSettings,
              ...allSettings
            } = state;

            const res = await fetch('/api/settings', {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
              },
              body: JSON.stringify(allSettings),
            });

            const result = await res.json().catch(() => ({}));
            if (!res.ok || !result.success) {
              const errMsg = result.error || 'Error al guardar ajustes en la nube';
              set({ isSyncing: false });
              return { success: false, error: errMsg };
            }

            const now = result.data?.updatedAt || new Date().toISOString();
            set({
              lastSyncedAt: now,
              isSyncing: false,
            });

            // Broadcast changes to all other open tabs in real time
            broadcastSettings(allSettings, now);

            return { success: true };
          } catch (err: any) {
            set({ isSyncing: false });
            return { success: false, error: err?.message || 'Error de conexión' };
          }
        },
      }),
      {
        name: 'fresha-settings',
        partialize: (state) => {
          const {
            isSyncing: _isSyncing,
            setSetting: _setSetting,
            setSettings: _setSettings,
            resetSettings: _resetSettings,
            getSetting: _getSetting,
            updateCrmSegmentTemplate: _updateCrmSegmentTemplate,
            removeCrmSegmentTemplate: _removeCrmSegmentTemplate,
            fetchRemoteSettings: _fetchRemoteSettings,
            saveRemoteSettings: _saveRemoteSettings,
            ...toPersist
          } = state;
          return toPersist as Settings;
        },
      }
    ),
    { name: 'SettingsStore' }
  )
);

// Listen for real-time broadcasts from other tabs/windows in the same browser
if (syncChannel) {
  syncChannel.onmessage = (event) => {
    if (event.data?.type === 'FRESHA_SETTINGS_SYNC' && event.data?.payload) {
      useSettingsStore.setState((state) => ({
        ...state,
        ...event.data.payload,
        lastSyncedAt: event.data.lastSyncedAt || state.lastSyncedAt,
        isSyncing: false,
      }));
    }
  };
}
